import { useCallback, useMemo } from "react";

import {
  type ContentEmailDynamicSlotsQuery,
  useContentEmailDynamicSlotsQuery,
} from "src/graphql";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";

import { parseSlotRow, type DynamicSlot } from "./types";

/**
 * Read the email's saved dynamic slots and narrow the loose jsonb rows into
 * typed `DynamicSlot`s at the boundary. Thin data hook — no local editing
 * state; the view owns any draft.
 *
 * While the agent is working (`pollWhileAgentWorks`), the query polls so
 * branches and content appear on the canvas as the agent writes them.
 */
export function useSavedDynamicSlots(
  emailId: string,
  options?: { pollWhileAgentWorks?: boolean },
) {
  const select = useCallback(
    (data: ContentEmailDynamicSlotsQuery): DynamicSlot[] =>
      data.contentEmailDynamicSlots
        .map(parseSlotRow)
        .filter((slot): slot is DynamicSlot => slot !== null),
    [],
  );

  const { refetchInterval } = useGovernedRefetchInterval<DynamicSlot[]>({
    baseMs: 4000,
  });

  const { data, isLoading, refetch } = useContentEmailDynamicSlotsQuery(
    { emailId },
    {
      select,
      enabled: Boolean(emailId),
      refetchInterval: options?.pollWhileAgentWorks ? refetchInterval : false,
    },
  );

  const slots = useMemo(() => data ?? [], [data]);

  return { slots, isLoading, refetch };
}
